import { Col } from "antd";
import { useCallback } from "react";
import { Form, SubmitButton, Select } from "formik-antd";
import { useTranslation } from "react-i18next";
import { WorkflowAction, Report } from "/app/src/models";
import HeroButton from "/app/src/components/HeroUi/Button";

/**
 * Renders a File component.
 *
 * @param {Object} props - The component props.
 * @param {Report[]} props.reports - List of reports.
 * @param {boolean} props.isNew - Flag indicating if the file is new.
 * @param {(action: WorkflowAction) => void} [props.removeWorkflowAction] - Callback to remove a workflow action.
 * @param {boolean} props.dirty - Indicates if there are unsaved changes.
 * @param {WorkflowAction} [props.action] - Optional workflow action.
 * @returns {JSX.Element} The rendered File component.
 */
export default function File({
  reports,
  isNew,
  removeWorkflowAction,
  dirty,

  action,
}: {
  reports: Report[];
  isNew: boolean;
  removeWorkflowAction?: (action: WorkflowAction) => void;
  dirty: boolean;
  action?: WorkflowAction;
}) {
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    if (removeWorkflowAction && action) {
      removeWorkflowAction(action);
    }
  }, [removeWorkflowAction, action]);

  return (
    <>
      <Col span={7}>
        <Form.Item name="reportId">
          <Select
            name="reportId"
            size="large"
            placeholder={t("translation:select_report")}
          >
            {reports.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={5}>
        {/* Placeholder Col, this keeps ui consistent when selecting different file attachments */}
      </Col>
      <Col span={5}>
        <SubmitButton type="primary" size="large" block disabled={!dirty}>
          {isNew ? t("translation:add_action") : t("translation:save")}
        </SubmitButton>
      </Col>
      {!isNew && (
        <Col span={3}>
          <HeroButton
            size="md"
            variant="bordered"
            color="default"
            fullWidth
            onClick={handleClick}
            className="hover:border-primary-default hover:text-primary-default bg-white"
          >
            {t("translation:remove")}
          </HeroButton>
        </Col>
      )}
    </>
  );
}
